<template>
  <b-container v-if="isFetching && isBlocked" class="message-list" fluid id="msglist">
  </b-container>
  <b-container v-else-if="isBlocked" class="message-list" fluid id="msglist">
    <div class="deleted-message">話題が存在しません。削除されたか、もしくは閲覧権がありません</div>
  </b-container>
  <b-container v-else class="message-list" fluid id="msglist">
    <message-page-header
      v-if="scrollJumpFlag"
      :titleLabel="titleLabel"
      :domainId="domainId"
      :topicId="topicId"
    />
    <b-row v-else class="message-topic-header" ref="topicHeader">
      <!-- モバイルUIでのピン -->
      <div class="left-option">
        <div class="left-pinned" v-if="pinned"><icon-pinned class="pinned"></icon-pinned></div>
        <span
          v-if="!!notification"
          class="dicon dicon-speaker-on topic-notification left-pinned"
        ></span>
      </div>
      <!-- タイトル画像 -->
      <div class="message-topic-icon">
        <profile-icon
          class="topic-list-item-icon rounded-circle m-auto"
          :userName="title"
          :image="icon"
          :topicId="topicId"
          :size="72"
          fontSize="32pt"
        />
      </div>
      <div class="message-topic-body">
        <!-- 話題ヘッダー -->
        <b-container class="d-flex flex-column category-container px-0">
          <b-row class="justify-content-center align-items-center" no-gutters>
            <!-- カテゴリーとピン -->
            <b-col class="d-inline-flex align-items-center text-nowrap">
              <span
                ref="topic-category-title"
                :class="`badge badge-secondary category ${categoryColor}`"
                >{{ categoryLabel }}</span
              >
              <span v-if="pinned" class="right-pinned mr-2"
                ><icon-pinned class="pinned"></icon-pinned
              ></span>
              <!-- ツールチップ(カテゴリ名省略時のみ表示) -->
              <b-tooltip
                @show="onTooltipShow($event, 'topic-category-title')"
                :target="() => $refs['topic-category-title']"
                :title="categoryLabel"
              />

              <span
                v-if="!!notification"
                class="dicon dicon-speaker-on topic-notification right-pinned mr-2"
              ></span>
            </b-col>

            <!-- 作成者情報 -->
            <b-col
              v-if="hasFeature('topic-owner-icon')"
              class="d-inline-flex align-items-center text-nowrap"
            >
              <profile-icon
                class="rounded-circle contributor-icon"
                :userName="contributor.name"
                :id="contributorId"
                :image="contributorIcon"
                :size="25"
              />
              <div class="contributor">{{ contributor.name }}</div>
            </b-col>
          </b-row>
        </b-container>
        <!-- タイトル -->
        <div ref="message-topic-title" class="message-topic-title">{{ titleLabel }}</div>
        <!-- 説明 -->
        <div
          class="message-topic-desc"
          v-html="
            $sanitize(descSource, { allowedAttributes: { a: ['href', 'name', 'target', 'rel'] } })
          "
        ></div>

        <!-- ツールチップ(タイトル省略時のみ表示) -->
        <b-tooltip
          @show="onTooltipShow($event, 'message-topic-title')"
          :target="() => $refs['message-topic-title']"
          :title="titleLabel"
        />
      </div>
      <div class="right-option">
        <!-- URLコピー&編集メニュー -->
        <div class="mr-2">
          <b-iconstack :id="topicEditId" class="three-dots" font-scale="1" tabindex="0"
            ><b-icon-three-dots
          /></b-iconstack>
          <b-popover ref="popover" :target="topicEditId" placement="bottom" triggers="focus">
            <div>
              <div class="edit-menu-item" v-on:click="onUrlCopyTopic">URLコピー</div>
              <div v-if="topicUpdatable" v-on:click="onTopicEditMessage" class="edit-menu-item">
                編集
              </div>
              <div v-if="topicDeletable" v-on:click="onTopicDeleteMessage" class="edit-menu-item">
                削除
              </div>
            </div>
          </b-popover>
        </div>
        <!-- お気に入り -->
        <div class="mr-2">
          <div v-if="topicStarFlag" v-on:click="onTopicStarClick">
            <b-iconstack class="star" font-scale="1"
              ><b-icon-star-fill class="has-star"
            /></b-iconstack>
          </div>
          <div v-else v-on:click="onTopicStarClick">
            <b-iconstack class="star" font-scale="1"
              ><b-icon-star-fill class="not-star"
            /></b-iconstack>
          </div>
          <div style="text-align: center; font-size: 12px">{{ topicStarCount }}</div>
        </div>
      </div>
    </b-row>

    <div v-if="!(messageList || []).length && !isFetching" class="no-messages">
      {{ noItemsError }}
    </div>

    <b-row v-for="message in messageList" :key="message.id">
      <message-list-item
        v-if="!message.deleted"
        :id="message.id"
        :domainId="message.domainId"
        :topicId="message.topicId"
        :title="message.title"
        :message="message.message"
        :star="message.star"
        :pinned="message.pinned"
        :icon="message.icon"
        :owner="message.owner"
        :updatedAt="message.updatedAt"
        :comments="message.comments"
        :photos="message.photos"
        :likes="message.likes"
        :like="message.like"
        :users="users"
        :viewer="viewer"
        :allow_attachment_type="allow_attachment_type"
        :acl="message.acl"
        :baseSort="baseSort"
        v-on:openModal="openModal"
        v-on:toEditMessage="toEditMessage"
        v-on:toDeleteMessage="toDeleteMessage"
      />
      <div class="deleted-message" v-else>
        {{ userName(message.deletedUser) }}により削除されました
      </div>
      <div style="width: 100%; height: 10px; background-color: #f4f4f4"></div>
    </b-row>
    <infinite-loading
      v-if="(messageList || []).length"
      class="mx-auto"
      :identifier="infiniteId"
      @infinite="infiniteHandler"
    >
      <span slot="no-results" />
      <span slot="no-more" />
    </infinite-loading>

    <delete-modal id="delete-modal" :messageId="deleteId" :viewer="viewer" type="message" />

    <!-- Topicの削除向け -->
    <delete-modal id="delete-topic-modal" :topicId="topicId" :viewer="viewer" type="topic" />

    <fab v-if="creatable" v-on:fab-clicked="fabClicked" />

    <topic-edit id="modal-topic" v-bind="topicEditParam" :isMobile="false" />

    <message-edit-modal
      id="modal-message"
      :type="type"
      :domainId="domainId"
      :topicId="topicId"
      :messageId="modalId"
      :title="modalTitle"
      :message="modalMessage"
      :photos="modalPhotos"
      :allow_attachment_type="allow_attachment_type"
      :acl="modalAcl"
      :topicAcl="topicAcl.clone()"
      :talkId="modalTalkId"
      :sendTalkOnCreate="modalSendTalkOnCreate"
      :sendTalkOnUpdate="modalSendTalkOnUpdate"
      v-on:interruptionViaModal="interruptionProcess"
    />

    <comment-edit-modal
      id="modal-comment"
      :commentId="commentEditModalParam.commentId"
      :messageId="commentEditModalParam.messageId"
      :message="commentEditModalParam.message"
      :photos="commentEditModalParam.photos"
      :shown="shownCommentEditModal"
      :allow_attachment_type="allow_attachment_type"
      v-on:interruptionComment="interruptionProcess"
    />
  </b-container>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import Vue from "vue";
import { useAppStore, useCategoryStore, useTopicStore, useUserStore } from "@/store";
import MessageListItem from "./MessageListItem.vue";
import "./message-list.scss";
import MessageEditModal from "./MessageEditModal.vue";
import IconPinned from "./icon/IconPinned.vue";
import ProfileIcon from "./ProfileIcon.vue";
import Fab from "./Fab.vue";
import type { Attachment, IconReaction } from "../model";
import { Acl, Category, Message, User } from "../model";
import { useTextProcess } from "@/composables/useTextProcess";
import { useClipboardCopy } from "@/composables/useClipboardCopy";
import { useEditDiscard } from "@/composables/useEditDiscard";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import { IconReactionType } from "../API";
import { COLOR_PALETTE, DEFAULT_COLOR } from "./color-palette";
import { SORT_TYPE } from "@/store";
import { Route, NavigationGuardNext } from "vue-router";
import DeleteModal from "./DeleteModal.vue";
import { ReactionController } from "../model/reaction-controller";
import { BPopover, BvModalEvent } from "bootstrap-vue";
import CommentEditModal from "./CommentEditModal.vue";
import { PhotoStreamArgs } from "./PhotoStream.vue";
import TopicEdit from "./TopicEdit.vue";
import MessagePageHeader from "./MessagePageHeader.vue";

import sanitizeHTML from "../sanitize";
import { allowCreateMessage, allowFeature, FeatureName } from "../direct-app-config";
import AclManager from "../model/acl-manager";
Vue.prototype.$sanitize = sanitizeHTML;

import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";

const INITIAL_DISPLAY_NUMBER = 20;

type TopicEditParam = {
  type: string;
  categories: Category[];
  topicId: string;
  domainId: string;
  title: string;
  desc: string;
  me: User;
  thumb?: Attachment;
  category?: Category;
  pinned: boolean;
  allow_attachment_type: AttachmentFileTypes;
  acl: Acl;
};

type CommentEditModalParam = {
  commentId: string;
  messageId: string;
  message: string;
  photos: string[];
  allow_attachment_type: AttachmentFileTypes;
};

export default defineComponent({
  name: "message-list",
  components: {
    MessageListItem,
    IconPinned,
    MessageEditModal,
    Fab,
    DeleteModal,
    ProfileIcon,
    CommentEditModal,
    TopicEdit,
    MessagePageHeader,
  },
  props: {
    domainId: { type: String, default: () => "", required: true }, //!< 所属組織ID
    topicId: { type: String, default: () => "", required: true }, //!< 所属話題ID
    icon: { default: () => "" as unknown as Attachment },
    category: { default: () => "カテゴリー" as unknown as Category, required: true },
    title: { type: String, default: () => "タイトル", required: true },
    desc: { type: String, default: () => "説明", required: true },
    // Topic関係
    pinned: { type: Boolean, default: () => false }, //!< Topicの固定状態
    topicOwner: { type: String, default: () => "" }, //!< Topicのオーナー情報(CognitoUserId)
    topicAcl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() }, //!< TopicのACL
    notification: { type: String, default: () => undefined }, //!< Topicの全体通知
    // 話題が削除されたか
    deleted: { type: Boolean, default: () => false },
    // 投稿リスト
    messages: { type: Array as PropType<Message[]>, default: () => [] },
    // ユーザー情報
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    // Reaction関係
    star: { type: Object as PropType<IconReaction>, default: () => undefined }, // Topicの★
    // ファイル添付設定
    allow_attachment_type: {
      default: () => AttachmentFileTypesDefault as AttachmentFileTypes,
    },
    // true: Fabボタンを表示する
    fab: { type: Boolean, default: () => true },
  },
  setup(props) {
    // 旧 TextProcessMixin / ClipboardCopyMixin / EditDiscardMixin → composable（1-D 利用側書き換え）
    const { highLightStr } = useTextProcess();
    const { onUrlCopy } = useClipboardCopy();
    const { routeLeaveEvent } = useEditDiscard();
    const rootEvent = useLegacyRootEvent();
    // $bvModal / $router / $route / $scrollTo / bv::* イベントは BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const isMobile = ref<boolean>(false);

    // 投稿編集画面への引数
    const type = ref<string>("create");
    const modalId = ref<string>("");
    const modalTitle = ref<string>("");
    const modalMessage = ref<string>("");
    const modalPhotos = ref<Attachment[]>([]);
    const modalAcl = ref<Acl>(Acl.createDummy());
    const modalTalkId = ref<string>("");
    const modalSendTalkOnCreate = ref<boolean>(false);
    const modalSendTalkOnUpdate = ref<boolean>(false);

    // コメント編集画面への引数
    const commentEditModalParam = ref<CommentEditModalParam>({
      commentId: "",
      messageId: "",
      message: "",
      photos: [],
      allow_attachment_type: AttachmentFileTypesDefault,
    });

    // 投稿削除モーダルへの引数
    const deleteId = ref<string>(""); // delete対象の投稿ID

    const shownTopicEditModal = ref<boolean>(false); // 話題編集モーダルの表示フラグ
    const shownMessageEditModal = ref<boolean>(false); // 投稿編集モーダルの表示フラグ
    const shownCommentEditModal = ref<boolean>(false); // コメント編集モーダルの表示フラグ

    const replaceParam = ref<{ domainId: string; topicId: string }>({ domainId: "", topicId: "" }); // router.replace用パラメータ

    const topicEditParam = ref<TopicEditParam>({
      type: "edit",
      categories: [],
      topicId: "",
      domainId: "",
      title: "",
      desc: "",
      me: User.createNotFoundUser(),
      thumb: undefined,
      category: undefined,
      pinned: false,
      allow_attachment_type: AttachmentFileTypesDefault,
      acl: Acl.createDummy(),
    });

    // Vuexの変更監視
    let unsubscribeMutation: (() => void) | undefined;
    let unsubscribeAction: (() => void) | undefined;

    // $refs.popover
    const popover = ref<BPopover | null>(null);

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    const isFetching = computed((): boolean => useAppStore().fetching);

    const isBlocked = computed((): boolean => {
      return props.deleted || (!props.topicId && !!vm?.$route);
    });

    // 権限: 投稿作成OKかどうか
    const creatable = computed((): boolean => {
      if (!props.fab) return false; // fab出さない設定の場合はそちらに準じる
      if (hasFeature("deny-edit") == false) return true; // 未対応組織
      return (
        AclManager.getCreateMessageAcl(AclManager.store, props.domainId, props.topicId) != "deny"
      );
    });

    const noItemsError = computed((): string => {
      // viwerがゲスト + 話題がゲスト閲覧不可 の場合
      return props.viewer.isGuestRole(props.domainId) && !topicAllowedGuest.value
        ? "ゲストの方はご覧いただけません"
        : "まだ投稿はありません";
    });

    const categoryLabel = computed((): string => {
      return props.category.title;
    });
    const categoryColor = computed((): string => {
      // カテゴリーなし
      if (!props.category) return "";

      const color = props.category.color;
      const index = COLOR_PALETTE.findIndex((c) => c == color);
      return index < 0 ? DEFAULT_COLOR : color;
    });
    const titleLabel = computed((): string => {
      return props.title;
    });
    // Topic作成者情報
    const contributor = computed((): User => {
      return findUserForCognitoUserId(props.topicOwner);
    });
    const contributorId = computed((): string => {
      return contributor.value.directId;
    });
    const contributorIcon = computed((): string => {
      return contributor.value.getProfileIconUrl();
    });

    const descSource = computed((): string => {
      return highLightStr(props.desc, undefined);
    });

    // Topicの★のON/OFF、★数
    const topicStarFlag = computed((): boolean => {
      return props.star?.userList.some((u) => u.userId == props.viewer.directId) || false;
    });
    const topicStarCount = computed((): number => {
      return props.star?.userList.length || 0;
    });

    // 編集/投稿画面のパス(mobileではない場合modalのためpathなし)
    const editPath = computed((): string => {
      return isMobile.value ? `/${props.domainId}/${props.topicId}/post-message` : "";
    });

    // 編集/投稿モーダル(mobileの場合は画面遷移のためなし)
    const editModalId = computed((): string => {
      return isMobile.value ? "" : "modal-message";
    });

    const topicUpdatable = computed((): boolean => {
      return AclManager.updateTopic(AclManager.store, topic.value);
    });
    const topicDeletable = computed((): boolean => {
      return AclManager.deleteTopic(AclManager.store, topic.value);
    });
    const topicEditId = computed((): string => {
      return `edit-button-${props.topicId}`;
    });
    const topicAllowedGuest = computed((): boolean => {
      if (!props.topicAcl) return false;
      else return props.topicAcl.guest.read;
    });
    const topic = computed(() => {
      return useTopicStore().getOne(props.domainId, props.topicId);
    });

    const infiniteId = ref<number>(0);
    const loading = ref<boolean>(false);
    const listRange = ref<{ top: number; bot: number }>({ top: 0, bot: INITIAL_DISPLAY_NUMBER });

    const messageList = computed((): Message[] => {
      if (scrollJumpFlag.value) {
        return props.messages.filter((mes) => mes.id === vm?.$route.params.messageId);
      } else {
        return props.messages.slice(listRange.value.top, listRange.value.bot);
      }
    });

    const scrollJumpFlag = computed((): boolean => {
      if (vm?.$route) {
        const msgId = vm.$route.params.messageId;
        const cmtId = vm.$route.params.commentId;
        if (msgId || cmtId)
          return true; // スクロールジャンプ有りにする
        else return false;
      } else {
        return false;
      }
    });

    // 個人設定のコメントソート順
    const baseSort = computed((): SORT_TYPE => {
      return (useAppStore().getSortCommentSetting(props.domainId) as SORT_TYPE) || "ASC";
    });

    const infiniteHandler = async (
      $state: import("vue-infinite-loading").StateChanger
    ): Promise<void> => {
      if (scrollJumpFlag.value || !vm?.$router) {
        $state.complete();
        return;
      }
      if (loading.value) return;
      loading.value = true;
      if (props.messages.length > messageList.value.length) {
        // 描画されてないデータがある > 表示範囲拡大
        listRange.value.bot += INITIAL_DISPLAY_NUMBER;
        $state.loaded();
        void nextTick(() => {
          loading.value = false;
        });
      } else {
        // storeにあるデータは全て描画されている
        $state.complete();
        void nextTick(() => {
          loading.value = false;
        });
      }
    };

    const userName = (directId: string): string => {
      return findUser(directId).name;
    };

    const findUser = (userDirectId: string): User => {
      const user = props.users.find((user) => user.directId == userDirectId);
      return user ? user : User.createNotFoundUser(userDirectId);
    };

    const findUserForCognitoUserId = (cognitoUserId: string): User => {
      const user = props.users.find((user) => user.id == cognitoUserId);
      return user ? user : User.createNotFoundUser(cognitoUserId);
    };

    const judgeMobile = (): void => {
      const mql = window.matchMedia("screen and (min-width: 768px)");
      isMobile.value = !mql.matches ? true : false;
    };

    const openModal = (value: string): void => {
      const message = props.messages.find((m) => m.id === value);
      if (!message) return;
      const param: PhotoStreamArgs = {
        files: message.photos,
        messageId: message.id,
        commentId: "",
      };
      rootEvent.emit("open-photo-stream", param);
    };

    /** Topicの★を押した時の処理 */
    const onTopicStarClick = (): void => {
      const ctl = new ReactionController();
      const t = ctl.findTopic(props.topicId);
      if (!t) return;
      ctl.onReactionClicked(t, IconReactionType.FAVORITE, props.viewer, topicStarFlag.value);
    };

    // タイトル省略時のツールチップを出すかどうかを判定する
    const onTooltipShow = (bvEvent: Event, id: string): void => {
      const target = vm?.$refs[id];
      if (target instanceof HTMLElement) {
        // webkit-line-clamp で省略された時、offsetHeight < scrollHeigt になる
        const oHeight = target.offsetHeight;
        const sHeight = target.scrollHeight;
        // text-overflow で省略されたとき、offsetWidth < scrollWidth になる
        const oWidth = target.offsetWidth;
        const sWidth = target.scrollWidth;
        if (oHeight < sHeight || oWidth < sWidth) return;
      }
      bvEvent.preventDefault(); // 出さない
    };

    // Urlコピー
    const onUrlCopyTopic = (): void => {
      (vm?.$refs.popover as BPopover).$emit("close"); // Close
      onUrlCopy({
        domainId: props.domainId,
        topicId: props.topicId,
        messageId: "",
        commentId: "",
      });
    };

    // Topicの編集メニュー
    const onTopicEditMessage = (): void => {
      const domainId = useAppStore().domainId as string;
      const topicId = props.topicId;
      if (isMobile.value) {
        vm?.$router.push(`/${domainId}/${topicId}/edit-topic`);
      } else {
        const categories = useCategoryStore().get(domainId) as Category[];
        topicEditParam.value = {
          type: "edit",
          categories: categories,
          topicId: props.topicId,
          domainId: props.domainId,
          title: props.title,
          desc: props.desc,
          me: props.viewer,
          thumb: props.icon,
          category: props.category,
          pinned: props.pinned,
          allow_attachment_type: props.allow_attachment_type,
          acl: props.topicAcl,
        };
        vm?.$bvModal.show("modal-topic");
      }
    };
    // Topicの削除メニュー
    const onTopicDeleteMessage = (): void => {
      vm?.$bvModal.show("delete-topic-modal");
    };

    // Fabボタンをクリックされたときの処理
    const fabClicked = (): void => {
      if (allowCreateMessage(props.domainId, props.topicId) == false) {
        rootEvent.emit("free-alert");
        return;
      }

      // モバイル版の場合は新規作成画面を開く
      // その他の場合はモーダル
      if (isMobile.value) {
        vm?.$router.push(editPath.value);
      } else {
        vm?.$bvModal.show(editModalId.value);
      }
    };

    /* 話題編集モーダルウィンドウを閉じた際の処理 */
    const closeTopicEditModal = (): void => {
      // 引数のリセット
      topicEditParam.value = {
        type: "edit",
        categories: [],
        topicId: "",
        domainId: "",
        title: "",
        desc: "",
        me: props.viewer,
        thumb: undefined,
        category: undefined,
        pinned: false,
        allow_attachment_type: AttachmentFileTypesDefault,
        acl: Acl.createDummy(),
      };
      shownTopicEditModal.value = false;
      console.log("topic-edit-modal closed.");
    };

    /* 投稿作成モーダルウィンドウを閉じた際の処理 */
    const closeMessageEditModal = (): void => {
      // 引数のリセット
      type.value = "create";
      modalId.value = "";
      modalTitle.value = "";
      modalMessage.value = "";
      modalPhotos.value = [];
      modalAcl.value = Acl.createDummy();
      modalTalkId.value = "";
      modalSendTalkOnCreate.value = false;
      modalSendTalkOnUpdate.value = false;
      shownMessageEditModal.value = false;
      console.log("message-edit-modal closed.");
    };

    /* コメント作成モーダルウィンドウを閉じた際の処理 */
    const closeCommentEditModal = (): void => {
      // 引数のリセット
      commentEditModalParam.value = {
        commentId: "",
        messageId: "",
        message: "",
        photos: [],
        allow_attachment_type: AttachmentFileTypesDefault,
      };
      shownCommentEditModal.value = false;
      console.log("comment-edit-modal closed.");
    };

    const interruptionProcess = (param: { id: string; path: string }): void => {
      vm?.$bvModal.hide(param.id);
      if (param.path) {
        vm?.$router.replace(param.path);
      }
    };

    const toEditMessage = (value: string): void => {
      const message = props.messages.find((m) => m.id === value);
      if (!message) return;
      if (isMobile.value) {
        const domainId = message.domainId;
        const topicId = message.topicId;
        vm?.$router.push(`/edit-message/${domainId}/${topicId}/${value}`);
      } else {
        // モーダル処理
        type.value = "edit";
        modalId.value = message.id;
        modalTitle.value = message.title;
        modalMessage.value = message.message;
        modalPhotos.value = message.photos;
        modalAcl.value = message.acl;
        modalTalkId.value = message.talkId;
        modalSendTalkOnCreate.value = message.sendTalkOnCreate;
        modalSendTalkOnUpdate.value = message.sendTalkOnUpdate;
        vm?.$bvModal.show("modal-message");
      }
    };

    const toDeleteMessage = (value: string): void => {
      deleteId.value = value;
      vm?.$bvModal.show("delete-modal");
    };

    const afterDeleteProcess = (): void => {
      if (vm?.$route.path === `/${replaceParam.value.domainId}`) {
        return;
      }
      vm?.$router.replace(`/${replaceParam.value.domainId}`);
    };

    // Feature
    const hasFeature = (keyword: FeatureName): boolean => {
      return allowFeature(keyword);
    };

    // created 相当
    judgeMobile();
    window.addEventListener("resize", judgeMobile);

    // Lifecycle
    onBeforeUnmount(() => {
      window.removeEventListener("resize", judgeMobile);
      vm?.$root.$off("bv::modal::show");
      vm?.$root.$off("bv::modal::hide");
      rootEvent.off("open-comment-edit-modal");
      if (unsubscribeMutation) unsubscribeMutation();
      if (unsubscribeAction) unsubscribeAction();
    });

    onMounted(() => {
      vm?.$root.$on("bv::modal::show", (bvEvent: BvModalEvent, modalId: string) => {
        switch (modalId) {
          case "modal-topic":
            shownTopicEditModal.value = true;
            console.log("topic-edit-modal opened.");
            break;
          case "modal-message":
            shownMessageEditModal.value = true;
            console.log("message-edit-modal opened.");
            break;
          case "modal-comment":
            shownCommentEditModal.value = true;
            console.log("comment-edit-modal opened.");
            break;
          default:
            break;
        }
      });
      vm?.$root.$on("bv::modal::hidden", (bvEvent: BvModalEvent, modalId: string) => {
        switch (modalId) {
          case "modal-topic":
            closeTopicEditModal();
            break;
          case "modal-message":
            closeMessageEditModal();
            break;
          case "modal-comment":
            closeCommentEditModal();
            break;
          default:
            break;
        }
      });
      rootEvent.on("open-comment-edit-modal", (param: CommentEditModalParam) => {
        // コメント編集モーダルを開く
        commentEditModalParam.value = param;
        vm?.$bvModal.show("modal-comment");
      });

      // 旧 Vuex の topics/delete mutation 監視を移植。
      // 当該 mutation は delete action / GraphQL サブスクリプション（onUpdate/onDelete）の
      // 双方から発火していたため、$onAction では捕捉しきれない。
      // 開いている話題が store から消えた（削除された）ことを $subscribe（state 変化）で検知する。
      const topicStore = useTopicStore();
      let topicWasPresent = !!topicStore.getOne(props.domainId, props.topicId);
      unsubscribeMutation = topicStore.$subscribe(() => {
        if (shownTopicEditModal.value || shownMessageEditModal.value || shownCommentEditModal.value)
          return;

        const present = !!topicStore.getOne(props.domainId, props.topicId);
        // 開いている話題が存在 -> 非存在に変化した = 削除された -> 話題一覧に移動
        if (topicWasPresent && !present) {
          const domainId = props.domainId;
          const topicId = props.topicId;
          replaceParam.value = { domainId: domainId, topicId: topicId };
          rootEvent.emit("show-error-modal", {
            msg: "話題が削除されたため、話題一覧に移動します",
            afterProcess: () => afterDeleteProcess(),
          });
        }
        topicWasPresent = present;
      });
      // 旧 Vuex の topics/invalidate action の before サブスクリプションを $onAction で移植。
      unsubscribeAction = topicStore.$onAction(({ name, args }) => {
        // invalidate以外は無視する
        if (name !== "invalidate") return;

        // 同topic以外は無視する
        const context = args[0] as {
          domainId: string;
          topicId: string;
          newAcl: Acl;
          oldAcl: Acl;
        };
        if (context.domainId !== props.domainId || context.topicId != props.topicId) return; // 関係無い

        // 読み取り権が変わってたら投稿一覧をロードする
        const me = useUserStore().me;
        const oldReadAuth = AclManager.readTopic(me, {
          domainId: props.domainId,
          acl: context.oldAcl,
        });
        const newReadAuth = AclManager.readTopic(me, {
          domainId: props.domainId,
          acl: context.newAcl,
        });
        if (newReadAuth && newReadAuth != oldReadAuth) {
          useAppStore().fetchData(context);
        }
      });
    });

    // immediate watch 群（全 const / computed / 関数定義後に登録: TDZ 回避）
    watch(
      () => props.topicId,
      (): void => {
        void nextTick(() => {
          if (vm?.$router) {
            vm?.$scrollTo("#msglist", 0, { container: "#msglist" });
          }
        });
      },
      { immediate: true }
    );

    return {
      isMobile,
      type,
      modalId,
      modalTitle,
      modalMessage,
      modalPhotos,
      modalAcl,
      modalTalkId,
      modalSendTalkOnCreate,
      modalSendTalkOnUpdate,
      commentEditModalParam,
      deleteId,
      shownTopicEditModal,
      shownMessageEditModal,
      shownCommentEditModal,
      replaceParam,
      topicEditParam,
      popover,
      allow_attachment,
      isFetching,
      isBlocked,
      creatable,
      noItemsError,
      categoryLabel,
      categoryColor,
      titleLabel,
      contributor,
      contributorId,
      contributorIcon,
      descSource,
      topicStarFlag,
      topicStarCount,
      editPath,
      editModalId,
      topicUpdatable,
      topicDeletable,
      topicEditId,
      topicAllowedGuest,
      topic,
      infiniteId,
      loading,
      listRange,
      messageList,
      scrollJumpFlag,
      baseSort,
      infiniteHandler,
      userName,
      findUser,
      findUserForCognitoUserId,
      judgeMobile,
      openModal,
      onTopicStarClick,
      onTooltipShow,
      onUrlCopyTopic,
      onTopicEditMessage,
      onTopicDeleteMessage,
      fabClicked,
      closeTopicEditModal,
      closeMessageEditModal,
      closeCommentEditModal,
      interruptionProcess,
      toEditMessage,
      toDeleteMessage,
      afterDeleteProcess,
      hasFeature,
      routeLeaveEvent,
    };
  },
  // ナビゲーション前にデータをロードする
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): void {
    if (!to.query.search) {
      useAppStore().fetchData({
        domainId: to.params.domainId,
        topicId: to.params.topicId,
        prevDomainId: from.params.domainId,
      });
    }
    next();
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): void {
    // パラメータのリセット
    useAppStore().setMessageId("");

    this.listRange = { top: 0, bot: INITIAL_DISPLAY_NUMBER };
    this.infiniteId += 1; // 無限ロードリセット

    if (
      from.params.domainId != to.params.domainId ||
      from.params.topicId != to.params.topicId ||
      from.params.messageId != to.params.messageId
    ) {
      useAppStore().fetchData({
        domainId: to.params.domainId,
        topicId: to.params.topicId,
        prevDomainId: from.params.domainId,
      });
    }
    next();
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    this.infiniteId += 1;
    this.routeLeaveEvent(next);
  },
});
</script>
